import classNames from 'classnames';
import type { RowHeights } from 'components/LazyRowContainer/RowHeights';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  JSX,
  MouseEvent,
  MutableRefObject,
  ReactNode,
} from 'react';
import './LazyRowContainer.css';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { RowWithOffset } from 'components/LazyRowContainer/helpers/RowWithOffset';
import { unsafeFindRowIndexAtOffset } from 'components/LazyRowContainer/helpers/unsafeFindRowIndexAtOffset';
import { ResizeObserverContainer } from 'components/ResizeObserverContainer/ResizeObserverContainer';
import { map } from 'fp-ts';
import deepEqual from 'react-fast-compare';
import { sum } from 'utils/number/sum';
import { useEffectOnChange } from 'utils/react/useEffectOnChange';

export type LazyRowContainerUpdateRangeFunction = (
  offset: number,
  clientHeight: number,
) => void;

/**
 * To be provided to LazyRowContainer externally. The component is designed to be agnostic to
 * particular scrolling container it appears in, the container has just to provide scroll offset
 * (usually scrollTop) and height (usually clientHeight).
 *
 * However we need to avoid passing scroll position via props, because triggering re-render on every
 * scroll step is a no-go. So we decided to call a function held in a ref in an imperative-like manner.
 *
 * The reference is filled upon mount via useLayoutEffect. To make the lazy row container work
 * properly, provider of the reference should call updateRangeRef with initial values on mount,
 * then on each scroll and resize step.
 */
export interface LazyRowContainerScrollContainerAdapter {
  updateRangeRef: MutableRefObject<LazyRowContainerUpdateRangeFunction | null>;
}

export interface LazyRowContainerHandle {
  getEndOffset: () => number;
}

export type RequestRowRangeEventHandler = (
  fromIndex: number,
  toIndex: number,
) => void;

export interface RowDragState {
  draggedIndex: number | null;
  dropBeforeIndex: number | null;
}

export interface LazyRowContainerProps {
  bodyWrapperClassName?: string;
  bodyWrapperStyle?: CSSProperties;
  className?: string;
  /**
   * Specify heights of rows different than default height
   *
   * This mechanism is currently optimized for scenarios where just a few rows have different height (e.g. user can
   * expand some row by click).
   *
   * You must use actual sparse array (e.g. remove entries with `delete`) to make this work properly.
   * We need to calculate sum and number of actually resized items in this array fast.
   */
  customRowHeights?: RowHeights;
  /**
   * Trigger data reload when the identity of this prop changes.
   *
   * Usage is up to the caller - request (filter set, ordering etc.) change,
   * signalled change of data on the server, etc.
   */
  dataReloadTrigger?: unknown;
  defaultRowHeight: number;
  /**
   * Header element that will stay fixed at top but scroll horizontally with content when needed
   *
   * Requirements to work properly:
   * - opaque background to prevent scrolling content flashing through
   * - fixed/min width matching content width
   */
  fixedHeader?: ReactNode;
  /**
   * Analogy to fixedHeader for bottom content
   *
   * @see fixedHeader
   */
  fixedFooter?: ReactNode;
  handleRef?: MutableRefObject<LazyRowContainerHandle | null>;
  /**
   * Display loading effect instead of actual rows
   *
   * The same thing happens automatically when rowCount === null.
   */
  loading?: boolean;
  /**
   * By default, row containers are stretched to at least scroll container width
   * to behave more like "just nested content in the scroll container" (so e.g. when
   * you add bottom border to the row, there will be no gap when the content is
   * narrower than scroll container).
   *
   * It can be suppressed by this attribute when rendering content narrower than
   * container width is desired (e.g. table cells of fixed width).
   */
  noStretchingRows?: boolean;
  /**
   * Emits sum of total content height (fixed header + footer + scrollable area inner height)
   *
   * Useful for implementing containers that render the table with dynamic height.
   */
  onContentHeightChange?: (contentHeight: number) => void;
  /**
   * Emitted whenever a range of rows (including both from and to index) is going to be rendered
   *
   * This is useful for lazy loading containers to detect when to load the data.
   * Containers that already have all data can ignore this event.
   */
  onRequestRowRange?: RequestRowRangeEventHandler;
  /**
   * Content to be rendered when rowCount = 0
   */
  renderEmptyContent?: () => ReactNode;
  renderLoader?: () => ReactNode;
  renderRow: (index: number) => ReactNode;
  /**
   * Allow customizing class for row containers rendered by this component
   *
   * Row containers take care of proper positioning of the row into the infinite scroll table.
   * Normally we wouldn't interfere such element and rather solve additional styling
   * inside row content, but we allow it here due to performance concerns of lazy rendered rows.
   *
   * Classes there should not change outer layouting of the element (position type, size etc),
   * they should be used only for styling inside row's border box (color, border etc).
   */
  rowContainerClassName?: string | ((rowIndex: number) => string);
  /**
   * When null is passed, loader is rendered and onRequestRowRange call
   * is fired as normally to load initial data.
   */
  rowCount: number | null;
  /**
   * By default, rows are positioned via `transform` for better performance.
   * However, this may cause trouble in some cases (e.g. `transform` forms new
   * stacking context so it will mess up z-indexes), in that case
   * you can disable the optimization and fall back to css left/top positioning.
   */
  disableCssTransformOptimization?: boolean;
  /** Visual state for drag-and-drop row reordering — drives CSS classes on row containers */
  rowDragState?: RowDragState;
  onRowMouseMove?: (rowIndex: number, e: MouseEvent) => void;
  onRowMouseLeave?: () => void;
  /**
   * See LazyRowContainerScrollContainerAdapter doc for details
   *
   * @see LazyRowContainerScrollContainerAdapter
   */
  scrollContainerAdapter: LazyRowContainerScrollContainerAdapter;
}

/**
 * Range of row indexes visible in the viewport, including end index
 *
 * The end index intentionally don't consider rowCount. This way we don't have
 * to recalculate the range when rowCount changes, but end must be capped
 * by rowCount - 1 at usages.
 */
interface VisibleRowRange {
  start: RowWithOffset;
  end: RowWithOffset;
}

const useFixedSection = (
  className: string,
  node: ReactNode | undefined,
  onUpdateHeight: () => void,
) => {
  // intentionally type-weak comparison of node falsiness used to detect optional elements in React template, like
  // fixedHeader={showFixedHeader && <...stuff...>} // false may come here and is supposed to show no fixed header

  const heightRef = useRef(0);
  const updateHeightRef = (height: number) => {
    if (heightRef.current !== height) {
      heightRef.current = height;
      onUpdateHeight();
    }
  };
  useEffect(() => {
    if (!node) {
      updateHeightRef(0);
    }
  });
  return {
    node: node ? (
      <ResizeObserverContainer
        className={className}
        onResize={(element) => {
          updateHeightRef(element.offsetHeight);
        }}
      >
        {node}
      </ResizeObserverContainer>
    ) : null,
    heightRef,
  };
};

/**
 * A container that renders only rows visible in the viewport of a scrolling parent
 */
export const LazyRowContainer = (props: LazyRowContainerProps): JSX.Element => {
  const customRowHeights = props.customRowHeights ?? map.empty;
  const [rowRange, setRowRange] = useState<VisibleRowRange | null>(null);
  /**
   * When custom heights change, offsets of rows get invalid. When another change (e.g. scroll) triggers updateRowRange
   * in the same subsequent rerender calling it with (useLastRowRange: true), a race condition appears:
   * when the (useLastRowRange: true) comes second, it uses invalid offsets and results in glitch NIM-14449.
   * This ref serves to immediately mark the range as dirty, so after custom heights change and are recalculated,
   * dirty heights are not used anymore.
   */
  const dirtyRowRange = useRef<VisibleRowRange | null>(null);
  const viewportRange = useRef({
    clientHeight: 0,
    startOffset: 0,
  });

  const emitContentHeightChange = () => {
    props.onContentHeightChange?.(
      totalHeight +
        fixedHeader.heightRef.current +
        fixedFooter.heightRef.current,
    );
  };

  const handleFixedSectionUpdate = () => {
    updateRowRange(true);
    emitContentHeightChange();
  };

  const fixedHeader = useFixedSection(
    'lazy-row-container__fixed-header',
    props.fixedHeader,
    handleFixedSectionUpdate,
  );
  const fixedFooter = useFixedSection(
    'lazy-row-container__fixed-footer',
    props.fixedFooter,
    handleFixedSectionUpdate,
  );
  const getEndOffset = () =>
    viewportRange.current.startOffset +
    viewportRange.current.clientHeight -
    fixedHeader.heightRef.current -
    fixedFooter.heightRef.current;

  if (props.handleRef) {
    props.handleRef.current = {
      getEndOffset,
    };
  }

  const totalHeight = useMemo(() => {
    if (props.rowCount === null) {
      return 0;
    }
    const customHeightValues = Array.from(customRowHeights.values());
    return (
      sum(customHeightValues) +
      (props.rowCount - customHeightValues.length) * props.defaultRowHeight
    ); // sum default height rows
  }, [props.rowCount, customRowHeights, props.defaultRowHeight]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally this way
  useEffect(() => {
    emitContentHeightChange();
  }, [totalHeight]);

  /**
   * Update range of visible rows based on stored viewport range and row dimensions from props.
   *
   * When viewport range changes, we can use indexes and offsets from last row range
   * to speed up the recalculation, but when row dimensions change, we must calculate it
   * from scratch. See unsafeFindRowIndexAtOffset docs for details about this optimization.
   */
  const updateRowRange = (useLastRowRange = false) => {
    if (!useLastRowRange) {
      dirtyRowRange.current = rowRange;
    }
    const canUseLastRowRange =
      useLastRowRange && dirtyRowRange.current !== rowRange;
    const newRange: VisibleRowRange = {
      start: unsafeFindRowIndexAtOffset(
        props.defaultRowHeight,
        customRowHeights,
        viewportRange.current.startOffset,
        canUseLastRowRange ? rowRange?.start : undefined,
      ),
      end: unsafeFindRowIndexAtOffset(
        props.defaultRowHeight,
        customRowHeights,
        getEndOffset(),
        canUseLastRowRange ? rowRange?.end : undefined,
      ),
    };
    if (rowRange === null || !deepEqual(rowRange, newRange)) {
      setRowRange(newRange);
    }
    return newRange;
  };

  useEffectOnChange(() => {
    // initial setting of row range should be triggered by scrollContainerAdapter calling updateRange on mount
    // after refs settle down, that's why we update here only on when row heights change.
    updateRowRange();
  }, [props.customRowHeights, props.defaultRowHeight]);

  const updateRange = useStableCallback(
    (offset: number, clientHeight: number) => {
      // Safari's overscroll feature can invoke this function with negative offset.
      // We just assume zero offset in such cases, we should just keep rows from zero loaded
      // as overscroll automatically bumps the UI back to zero.
      const positiveOffset = Math.max(0, offset);
      if (
        positiveOffset === viewportRange.current.startOffset &&
        clientHeight === viewportRange.current.clientHeight
      ) {
        return;
      }
      viewportRange.current.startOffset = positiveOffset;
      viewportRange.current.clientHeight = clientHeight;
      updateRowRange(true);
    },
  );

  useLayoutEffect(() => {
    // Adapter managing props.scrollContainerAdapter is expected to run the update function
    // in useEffect after mount to set initial range, so we should set the reference before that
    // happens - that's why useLayoutEffect.
    props.scrollContainerAdapter.updateRangeRef.current = updateRange;
    return () => {
      props.scrollContainerAdapter.updateRangeRef.current = null;
    };
  }, [props.scrollContainerAdapter.updateRangeRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally this way
  useEffect(() => {
    if (rowRange !== null) {
      let appliedRowRange = rowRange;
      if (props.rowCount === null) {
        // this means that we load a new dataset and content will be scrolled to top,
        // so avoid extra request for data from some scrolled position.
        viewportRange.current.startOffset = 0;
        appliedRowRange = updateRowRange(true);
      }
      props.onRequestRowRange?.(
        appliedRowRange.start.index,
        appliedRowRange.end.index,
      );
    }
  }, [rowRange, props.rowCount, props.dataReloadTrigger]);

  const renderRowContainer = (
    rowIndex: number,
    rowOffset: number,
    rowHeight: number,
  ) => (
    <div
      className={classNames(
        'lazy-row absolute',
        !props.noStretchingRows && 'min-w-full',
        typeof props.rowContainerClassName === 'function'
          ? props.rowContainerClassName(rowIndex)
          : props.rowContainerClassName,
        rowIndex % 2 === 0 && 'even-row',
        props.rowDragState?.draggedIndex === rowIndex &&
          'ipf-table__row--dragged',
        rowIndex === 0 &&
          props.rowDragState?.dropBeforeIndex === 0 &&
          'ipf-table__row--drop-before',
        props.rowDragState?.dropBeforeIndex === rowIndex + 1 &&
          'ipf-table__row--drop-after',
      )}
      key={rowIndex}
      onMouseMove={
        props.onRowMouseMove
          ? (e) => props.onRowMouseMove!(rowIndex, e)
          : undefined
      }
      onMouseLeave={props.onRowMouseLeave}
      style={{
        height: rowHeight,
        ...(props.disableCssTransformOptimization
          ? { top: rowOffset }
          : { transform: `translateY(${rowOffset}px)` }),
      }}
    >
      {props.renderRow(rowIndex)}
    </div>
  );

  const tableHasContent = (props.rowCount ?? 0) > 0;

  const renderRows = () => {
    if (!rowRange) {
      return null;
    }
    if (props.loading || props.rowCount === null) {
      return props.renderLoader?.() ?? <LoaderAndText />;
    }
    if (props.rowCount === 0) {
      return props.renderEmptyContent?.();
    }
    const result = [];
    const maxIndex = Math.min(rowRange.end.index, props.rowCount - 1);
    for (
      let rowIndex = rowRange.start.index, rowOffset = rowRange.start.offset;
      rowIndex <= maxIndex;
      rowIndex += 1
    ) {
      const rowHeight =
        customRowHeights.get(rowIndex) ?? props.defaultRowHeight;
      result[rowIndex - rowRange.start.index] = renderRowContainer(
        rowIndex,
        rowOffset,
        rowHeight,
      );
      rowOffset += rowHeight;
    }
    return result;
  };

  return (
    <div className={classNames('isolate', props.className)}>
      {fixedHeader.node}
      <div
        className={classNames(
          'relative',
          props.bodyWrapperClassName,
          tableHasContent && !props.loading
            ? 'lazy-row-container__body--non-empty'
            : 'lazy-row-container__body--empty',
        )}
        style={{
          ...(tableHasContent && !props.loading
            ? { height: totalHeight }
            : undefined),
          ...props.bodyWrapperStyle,
        }}
      >
        {renderRows()}
      </div>
      {fixedFooter.node}
    </div>
  );
};
